import React, { useState, useEffect } from "react";

// internal imports
import { validateResetPasswordLink, resetPassword } from "../../services/api";
import "./ResetPassword.css";
import CopyRight from "../copyright/CopyRight";
import SnackbarComp from "../Snackbar/SnackbarComp";
import colors from "../../theme";

// external imports
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useLocation, useNavigate } from "react-router-dom";

export default function ResetPassword() {
  const [error, setError] = useState(null);
  const [statusCode, setStatusCode] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get("code");
  const key = queryParams.get("key");
  const [isLinkValid, setIsLinkValid] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(true);
  const [showConfirmPassword, setShowConfirmPassword] = useState(true);
  const [errors, setErrors] = useState({});

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 1000);

    const checkLinkValidity = async () => {
      try {
        const isLinkValid = await validateResetPasswordLink(token, key);
        setIsLinkValid(isLinkValid);
        if (!isLinkValid) {
          console.error("Link validation failed");
        }
      } catch (error) {
        setIsLinkValid(false);
        console.error("Error validating link:", error);
      }
    };

    checkLinkValidity();

    return () => clearTimeout(timer);
  }, [token, key]);

  const validatePassword = (password) => {
    const passwordRegex =
      /^(?=.*[A-Z])(?=.*\d)(?=.*[~!@#$%^&*()_\-+=\[\]{}\\/|<>?])[A-Za-z\d~!@#$%^&*()_\-+=\[\]{}\\/|<>?]{8,}$/; // eslint-disable-line
    return passwordRegex.test(password);
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    const newErrors = {};

    if (!password) {
      newErrors.password = "Password is required";
    } else if (!validatePassword(password)) {
      newErrors.password =
        "Password must be at least 8 characters long, contain an uppercase letter, a number, and a special character";
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = "Confirm password is required";
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    try {
      const response = await resetPassword(password, token, key);
      if (response.status === "0100") {
        navigate("/login");
      } else {
        setError(response.message);
        setStatusCode(response.status);
        setOpenSnackbar(true);
      }
    } catch (error) {
      console.error("Password reset error:", error);
      setError(error.message || "An unknown error occurred");
      setStatusCode(error.status || 500);
      setOpenSnackbar(true);
    }
  };
  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event) => event.preventDefault();
  const handleClickShowConfirmPassword = () =>
    setShowConfirmPassword((show) => !show);
  const handleMouseDownConfirmPassword = (event) => event.preventDefault();

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress color="success" />
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
      }}
    >
      <Container component="main" maxWidth="xs">
        <CssBaseline />
        {isLinkValid ? (
          <Box
            sx={{
              marginTop: 6,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography
              component="h1"
              variant="h5"
              sx={{ marginBottom: "10px", fontWeight: "bold" }}
            >
              PASSWORD RESET
            </Typography>

            <Typography
              variant="body1"
              align="center"
              sx={{ marginBottom: "20px", color: "#555" }}
            >
              Set a new password
            </Typography>
            <Box
              component="form"
              onSubmit={handleLogin}
              sx={{ mt: 1 }}
              noValidate
            >
              <FormControl
                required
                fullWidth
                variant="outlined"
                error={!!errors.password}
                sx={{ marginBottom: 2 }}
              >
                <InputLabel htmlFor="password">New Password</InputLabel>
                <OutlinedInput
                  id="password"
                  name="password"
                  type={!showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                  label="New Password"
                  sx={{ borderRadius: "20px", height: "3.5rem" }}
                />
              </FormControl>
              <FormControl
                required
                fullWidth
                variant="outlined"
                error={!!errors.confirmPassword || !!errors.password}
              >
                <InputLabel htmlFor="confirmPassword">
                  Confirm Password
                </InputLabel>
                <OutlinedInput
                  id="confirmPassword"
                  name="confirmPassword"
                  type={!showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowConfirmPassword}
                        onMouseDown={handleMouseDownConfirmPassword}
                        edge="end"
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  }
                  label="Confirm Password"
                  sx={{ borderRadius: "20px", height: "3.5rem" }}
                />
                {errors.confirmPassword && (
                  <Typography variant="body2" color="error">
                    {errors.confirmPassword}
                  </Typography>
                )}
                {errors.password && (
                  <Typography variant="body2" color="error">
                    {errors.password}
                  </Typography>
                )}
              </FormControl>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  fontWeight: "bold",
                  borderRadius: "20px",
                  height: "3.5rem",
                  marginTop: "20px",
                  backgroundColor: colors.primary,
                  "&:hover": {
                    backgroundColor: colors.hoverButton,
                  },
                }}
              >
                Change Password
              </Button>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              marginTop: 6,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography
              variant="h5"
              align="center"
              sx={{ marginBottom: "20px", fontWeight: "bold" }}
            >
              Link is invalid
            </Typography>
          </Box>
        )}
        <CopyRight sx={{ mt: 4, mb: 4 }} />
      </Container>
      <SnackbarComp
        open={openSnackbar}
        onClose={handleCloseSnackbar}
        errorMessage={error || Object.values(errors).join(". ")}
        statusCode={statusCode}
      />
    </div>
  );
}
